import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import './ConfirmDialog.scss';

export type ConfirmTone = 'danger' | 'warning' | 'info';

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  // Resolved on user click. May be async — the dialog stays open + the
  // confirm button shows a busy state until it resolves, then closes.
  // Throwing inside surfaces the error inline (no toast — let the caller
  // decide whether to also toast).
  onConfirm: () => void | Promise<void>;

  title: string;
  // Body content. Either a string (single paragraph) or any ReactNode for
  // richer layouts (impact bullets, callouts, etc.).
  description?: ReactNode;

  // Visual tone of the icon + confirm button. Danger = red trash-style;
  // warning = amber; info = neutral. Default is "warning".
  tone?: ConfirmTone;

  confirmLabel?: string;
  cancelLabel?: string;
}

const toneIcon = (tone: ConfirmTone) =>
  tone === 'info' ? <Info size={18} /> : <AlertTriangle size={18} />;

export const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  tone = 'warning',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
}: ConfirmDialogProps) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset busy/error when the dialog opens so a previous error doesn't
  // greet the user on a fresh open.
  useEffect(() => {
    if (open) {
      setBusy(false);
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || (e as Error)?.message || 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  const confirmVariant = tone === 'danger' ? 'danger' : 'primary';

  return (
    <Modal
      open={open}
      onClose={busy ? () => undefined : onClose}
      title={title}
      size="sm"
      closeOnBackdrop={!busy}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={confirmVariant}
            onClick={handleConfirm}
            loading={busy}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className={`confirm-dialog confirm-dialog--${tone}`}>
        <div className="confirm-dialog__icon" aria-hidden="true">
          {toneIcon(tone)}
        </div>
        <div className="confirm-dialog__body">
          {/* String descriptions render as a single styled <p>; ReactNode
              descriptions get the same `.confirm-dialog__text` wrapper so
              consumers can pass <p>…</p> without per-call inline styling. */}
          {description &&
            (typeof description === 'string' ? (
              <p className="confirm-dialog__text">{description}</p>
            ) : (
              <div className="confirm-dialog__text">{description}</div>
            ))}
          {error && (
            <div className="confirm-dialog__error" role="alert">
              {error}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
